import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeWorkLocation } from "types/api-types/payroll/everee-work-locations/everee-work-locations.types";

/**
 * EvereeWorkLocationsStore - Manages work location data for admin interface
 */
class EvereeWorkLocationsStore {
  rootStore: RootStore;

  // Observable Properties
  isLoading = false;
  error: string | null = null;
  searchTerm = "";
  activeFilter: boolean | null = null;
  selectedLocationId: string | null = null;

  // DataGrid state - uses DataGridColumnHeader format (key, title, width)
  currentHeaders: Array<{ key: string; title: string; width: number }> = [
    { key: "name", title: "Name", width: 200 },
    { key: "city", title: "City", width: 140 },
    { key: "state", title: "State", width: 80 },
    { key: "postalCode", title: "ZIP", width: 100 },
    { key: "isActive", title: "Active", width: 80 },
    { key: "createdAt", title: "Created", width: 140 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessor
  get evereeWorkLocationsStore(): ServiceStore {
    return this.rootStore.evereeWorkLocationsStore as ServiceStore;
  }

  // Computed Values
  get locations(): evereeWorkLocation[] {
    return (this.evereeWorkLocationsStore?.entities || []) as unknown as evereeWorkLocation[];
  }

  get filteredLocations(): evereeWorkLocation[] {
    let result = this.locations;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(
        (l) =>
          l.name?.toLowerCase().includes(term) ||
          l.city?.toLowerCase().includes(term) ||
          l.state?.toLowerCase().includes(term)
      );
    }

    if (this.activeFilter !== null) {
      result = result.filter((l) => l.isActive === this.activeFilter);
    }

    return result;
  }

  get dataGridData(): Record<string, unknown>[] {
    return this.filteredLocations.map((l) => ({
      id: l.evereeWorkLocationId,
      evereeWorkLocationId: l.evereeWorkLocationId,
      name: l.name,
      line1: l.line1,
      line2: l.line2 || "",
      city: l.city,
      state: l.state,
      postalCode: l.postalCode,
      isActive: l.isActive ? "Yes" : "No",
      createdAt: l.createdAt ? new Date(l.createdAt).toLocaleDateString() : "",
    }));
  }

  get totalCount(): number {
    return this.locations.length;
  }

  get filteredCount(): number {
    return this.filteredLocations.length;
  }

  get activeLocations(): evereeWorkLocation[] {
    return this.locations.filter((l) => l.isActive);
  }

  // Actions
  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setActiveFilter = (active: boolean | null) => {
    this.activeFilter = active;
  };

  setSelectedLocationId = (locationId: string | null) => {
    this.selectedLocationId = locationId;
  };

  clearFilters = () => {
    this.searchTerm = "";
    this.activeFilter = null;
  };

  // Async Actions
  loadLocations = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // Build query with company filter if provided
      const query: Record<string, unknown> = { $limit: 1000 };
      if (companyId) {
        query.companyId = companyId;
      }

      await this.evereeWorkLocationsStore.find({ query });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load locations";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createLocation = async (data: {
    name: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
  }): Promise<boolean> => {
    try {
      await this.evereeWorkLocationsStore.create(data);
      await this.loadLocations();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create location";
      });
      return false;
    }
  };

  updateLocation = async (
    locationId: string,
    data: Partial<evereeWorkLocation>
  ): Promise<boolean> => {
    try {
      await this.evereeWorkLocationsStore.patch(locationId, data);
      await this.loadLocations();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update location";
      });
      return false;
    }
  };

  toggleLocationActive = async (locationId: string): Promise<boolean> => {
    const location = this.locations.find(
      (l) => l.evereeWorkLocationId === locationId
    );
    if (!location) return false;

    return this.updateLocation(locationId, { isActive: !location.isActive });
  };

  deleteLocation = async (locationId: string): Promise<boolean> => {
    try {
      await this.evereeWorkLocationsStore.remove(locationId);
      await this.loadLocations();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to delete location";
      });
      return false;
    }
  };

  reset = () => {
    this.isLoading = false;
    this.error = null;
    this.searchTerm = "";
    this.activeFilter = null;
    this.selectedLocationId = null;
  };
}

export default EvereeWorkLocationsStore;
